import './css/CalificacionesGeneral.css';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { show_alerta, getImgUrl, esCalificacionBaja, esCalificacionIndice2, obtenerEstadoInstancias } from '../../funciones.js';
import CONFIG from '../../config.js';

const ESTADO_ICONO = {
    'violeta': 'fa-circle-check',
    'verde': 'fa-thumbs-up',
    'amarillo': 'fa-clock',
    'rojo': 'fa-triangle-exclamation'
};

const URL_COHORTES = `${CONFIG.API_URL}/operarCohortes.php`;
const URL_ESTUDIANTES = `${CONFIG.API_URL}/operarEstudiantes.php`;
const URL_CALIFICACIONES = `${CONFIG.API_URL}/operarCalificacionesGeneral.php`;

// ── Helpers ────────────────────────────────────────────────────────────────
/**
 * Determina el estado visual de una calificación de un estudiante.
 * @param {object|undefined} valoracion - fila de valoracion del alumno
 * @param {boolean} aprobada - si fue aprobada por el admin
 * @param {boolean} publicada - si fue publicada
 */
function calcularEstado(valoracion, aprobada, publicada) {
    if (publicada || (valoracion && (valoracion.estado_aprobacion === 'publicada' || valoracion.estado_aprobacion === 'vista_por_tutor'))) return 'publicada';
    if (aprobada || (valoracion && valoracion.estado_aprobacion === 'aprobada')) return 'aprobada';
    if (valoracion && valoracion.valor !== null && valoracion.valor !== '') return 'cargada';
    return 'sin-cargar';
}

const ESTADO_LABEL = {
    'sin-cargar': { texto: 'Sin cargar', icon: 'fa-circle-xmark' },
    'cargada': { texto: 'Cargada', icon: 'fa-circle-check' },
    'aprobada': { texto: 'Aprobada', icon: 'fa-thumbs-up' },
    'publicada': { texto: 'Publicada', icon: 'fa-bullhorn' },
};

function BadgeEstado({ estado, esVistaPorTutor }) {
    const { texto, icon } = ESTADO_LABEL[estado] || ESTADO_LABEL['sin-cargar'];
    return (
        <span className={`calgen-badge ${estado}`}>
            <i className={`fa-solid ${icon}`}></i>
            {texto}
            {esVistaPorTutor && (
                <i className="fa-solid fa-eye text-success ms-1" title="Vista por el tutor"></i>
            )}
        </span>
    );
}

// ──────────────────────────────────────────────────────────────────────────
function CalificacionesGeneral({ acceder, rol, configuracion }) {
    const navigate = useNavigate();

    // ── Control de Seguridad por Rol (Permitidos: 1, 2, 3, 4, 10) ──
    useEffect(() => {
        const rolActual = String(rol || localStorage.getItem('loggeduserRolId') || localStorage.getItem('loggeduserRol') || '');
        const rolesPermitidos = ['1', '2', '3', '4', '10'];
        if (rolActual && !rolesPermitidos.includes(rolActual)) {
            show_alerta('Acceso denegado. Este módulo es exclusivo para los roles autorizados (1, 2, 3, 4 y 10).', 'warning');
            navigate('/Principal');
        }
    }, [rol, navigate]);

    const esPreceptor = String(rol || localStorage.getItem('loggeduserRolId') || localStorage.getItem('loggeduserRol') || '') === '10';

    // ── Filtros ──────────────────────────────────────────────────────────
    const [cohortes, setCohortes] = useState([]);
    const [cohorteId, setCohorteId] = useState('');
    const [cursosDisp, setCursosDisp] = useState([]);
    const [cursoGrupo, setCursoGrupo] = useState(''); // "orden-seccion"
    const [gruposDisp, setGruposDisp] = useState([]);
    const [division, setDivision] = useState(''); // división seleccionada (letra)
    const [instancias, setInstancias] = useState([]);
    const [instanciasDefault, setInstanciasDefault] = useState([]);
    const [instanciaId, setInstanciaId] = useState('');
    const [grupo, setGrupo] = useState(''); // id_curso_grupo numérico o "todos"
    const [cursoId, setCursoId] = useState(''); // id_curso numérico o "todos"

    // ── Datos del panel ──────────────────────────────────────────────────
    const [estudiantes, setEstudiantes] = useState([]);
    const [valoraciones, setValoraciones] = useState([]);
    const [equipoDocente, setEquipoDocente] = useState([]);
    const [trayectorias, setTrayectorias] = useState([]);
    const [loading, setLoading] = useState(false);

    // ── Estados para opción "Todos los espacios" ───────────────────────
    const [resumenEspacios, setResumenEspacios] = useState([]);
    const [matrizEstudiantes, setMatrizEstudiantes] = useState([]);
    const [modoVerMatriz, setModoVerMatriz] = useState(false);
    const [loadingTodos, setLoadingTodos] = useState(false);

    // ── Estado de aprobaciones y publicaciones (local) ──────────────────
    const [aprobadas, setAprobadas] = useState({});
    const [publicadas, setPublicadas] = useState(false);

    // ── Cargar cohortes al montar ────────────────────────────────────────
    useEffect(() => {
        const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');
        const nivelParam = loggeduserNivelId ? `&id_nivel=${loggeduserNivelId}` : '';
        axios.get(`${URL_COHORTES}?accion=listar${nivelParam}`)
            .then(res => {
                if (res.data.success && Array.isArray(res.data.cohortes)) {
                    setCohortes(res.data.cohortes);
                    const anioActual = String(new Date().getFullYear());
                    const cohorteInicial = res.data.cohortes.find(c => String(c.año) === anioActual) || res.data.cohortes[0];
                    if (cohorteInicial && cohorteInicial.id) {
                        axios.get(`${CONFIG.API_URL}/operarInstancias.php?id_cohorte=${cohorteInicial.id}`)
                            .then(resInst => {
                                if (resInst.data && resInst.data.success) {
                                    setInstanciasDefault(resInst.data.instancias || []);
                                }
                            })
                            .catch(() => {});
                    }
                }
            })
            .catch(() => show_alerta('Error al cargar cohortes', 'error'));
    }, []);

    // ── Al cambiar cohorte → cargar cursos e instancias disponibles ───────
    useEffect(() => {
        if (!cohorteId) {
            setCursosDisp([]);
            setCursoGrupo('');
            setGruposDisp([]);
            setDivision('');
            setInstancias([]);
            setInstanciaId('');
            setGrupo('');
            setCursoId('');
            resetPanel();
            return;
        }

        // Cargar cursos
        axios.get(`${URL_ESTUDIANTES}?cohorte=${cohorteId}&curso_grupo=0`)
            .then(res => {
                if (res.data.cursos) {
                    setCursosDisp(res.data.cursos);
                }
            })
            .catch(() => show_alerta('Error al cargar cursos', 'error'));

        // Cargar instancias
        axios.get(`${CONFIG.API_URL}/operarInstancias.php?id_cohorte=${cohorteId}`)
            .then(res => {
                if (res.data.success) {
                    setInstancias(res.data.instancias || []);
                }
            })
            .catch(() => show_alerta('Error al cargar instancias', 'error'));

        setCursoGrupo('');
        setGruposDisp([]);
        setDivision('');
        setInstancias([]);
        setInstanciaId('');
        setGrupo('');
        setCursoId('');
        resetPanel();
    }, [cohorteId]);

    // ── Al cambiar curso → filtrar grupos (secciones) disponibles ────────
    useEffect(() => {
        if (!cursoGrupo || !cohorteId) {
            setGruposDisp([]);
            setDivision('');
            setGrupo('');
            setCursoId('');
            resetPanel();
            return;
        }
        const [orden] = cursoGrupo.split('-');
        const uniqueSecciones = [];
        const seen = new Set();
        cursosDisp.forEach(c => {
            if (String(c.orden) === String(orden) && !seen.has(c.seccion)) {
                seen.add(c.seccion);
                uniqueSecciones.push(c);
            }
        });
        setGruposDisp(uniqueSecciones);
        setDivision('');
        setGrupo('');
        setCursoId('');
        resetPanel();
    }, [cursoGrupo, cursosDisp, cohorteId]);

    // ── Al cambiar división → resetear materia/grupo/cursoId ─────────────
    useEffect(() => {
        setGrupo('');
        setCursoId('');
        resetPanel();
    }, [division]);

    // ── Al elegir grupo/materia/instancia → cargar estudiantes/panel o todos ────
    useEffect(() => {
        if (!grupo || !cohorteId || !instanciaId) {
            resetPanel();
            return;
        }
        if (grupo === 'todos') {
            cargarTodosLosEspacios();
        } else if (cursoId) {
            cargarPanel();
        }
    }, [grupo, cursoId, instanciaId, cohorteId]);

    const resetPanel = () => {
        setEstudiantes([]);
        setValoraciones([]);
        setEquipoDocente([]);
        setTrayectorias([]);
        setResumenEspacios([]);
        setMatrizEstudiantes([]);
        setAprobadas({});
        setPublicadas(false);
    };

    // ── Cargar todos los espacios curriculares ("Todos los espacios") ──────
    const cargarTodosLosEspacios = useCallback(async () => {
        setLoadingTodos(true);
        try {
            const [orden] = cursoGrupo ? cursoGrupo.split('-') : [''];
            const materias = cursosDisp.filter(
                c => String(c.orden) === String(orden) && String(c.seccion) === String(division)
            );

            if (materias.length === 0) {
                setResumenEspacios([]);
                setMatrizEstudiantes([]);
                return;
            }

            const requests = materias.map(m =>
                Promise.all([
                    axios.post(URL_ESTUDIANTES, {
                        modo: 'buscarEstudiantesCurso',
                        id_curso: m.id_curso,
                        id_grupo: m.id,
                        id_instancia: instanciaId,
                    }),
                    axios.get(`${CONFIG.API_URL}/operarTrayectorias.php?id_curso_grupo=${m.id}`)
                        .catch(() => ({ data: [] }))
                ])
            );

            const responses = await Promise.all(requests);
            
            const resumen = [];
            const estudiantesMap = new Map();

            responses.forEach(([resEst, resTray], index) => {
                const m = materias[index];
                const listEst = resEst.data.estudiantes || [];
                const listVal = resEst.data.valoraciones || [];
                const listDoc = resEst.data.equipo_docente || [];
                const listTray = Array.isArray(resTray.data) ? resTray.data : [];

                const trayAsociada = listTray.find(t => String(t.id_instancia) === String(instanciaId));

                const totalEst = listEst.length;
                let cargadas = 0;
                let aprobadas = 0;
                let publicadas = 0;

                listEst.forEach(est => {
                    const val = listVal.find(v => String(v.id_usuario) === String(est.id));
                    const tieneCal = val && val.valor !== null && val.valor !== '' && String(val.valor).trim() !== '';
                    const esAprob = val && (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada' || val.estado_aprobacion === 'vista_por_tutor');
                    const esPub = val && (val.estado_aprobacion === 'publicada' || val.estado_aprobacion === 'vista_por_tutor');

                    if (tieneCal) cargadas++;
                    if (esAprob) aprobadas++;
                    if (esPub) publicadas++;

                    if (!estudiantesMap.has(est.id)) {
                        estudiantesMap.set(est.id, {
                            id: est.id,
                            nombre: est.nombre,
                            apellido: est.apellido,
                            apodo: est.apodo,
                            valoraciones: {}
                        });
                    }
                    estudiantesMap.get(est.id).valoraciones[m.id] = {
                        valObj: val,
                        valor: val?.valor || '',
                        estado: val?.estado_aprobacion || null
                    };
                });

                // Clasificación de Color según directiva del usuario:
                // 2.1 Violeta: todo cargado, aceptado y publicado
                // 2.2 Verde: todo cargado y aceptado (aprobado), pero no publicado
                // 2.3 Amarillo: todo cargado pero falta aceptar/aprobar
                // 2.4 Rojo: falta cargar notas (incompleto)
                let estadoCodigo = 'rojo';
                let estadoTexto = 'Falta cargar notas';
                let badgeClass = 'badge-estado-rojo';
                let cardClass = 'card-espacio-rojo';

                if (totalEst > 0 && cargadas === totalEst) {
                    if (publicadas === totalEst) {
                        estadoCodigo = 'violeta';
                        estadoTexto = 'Todo cargado, aceptado y publicado';
                        badgeClass = 'badge-estado-violeta';
                        cardClass = 'card-espacio-violeta';
                    } else if (aprobadas === totalEst) {
                        estadoCodigo = 'verde';
                        estadoTexto = 'Todo cargado y aceptado (sin publicar)';
                        badgeClass = 'badge-estado-verde';
                        cardClass = 'card-espacio-verde';
                    } else {
                        estadoCodigo = 'amarillo';
                        estadoTexto = 'Todo cargado (falta aprobar por gestión)';
                        badgeClass = 'badge-estado-amarillo';
                        cardClass = 'card-espacio-amarillo';
                    }
                }

                resumen.push({
                    materia: m,
                    totalEst,
                    cargadas,
                    aprobadas,
                    publicadas,
                    estadoCodigo,
                    estadoTexto,
                    badgeClass,
                    cardClass,
                    equipoDocente: listDoc,
                    trayectoria: trayAsociada || null
                });
            });

            setResumenEspacios(resumen);
            setMatrizEstudiantes(Array.from(estudiantesMap.values()).sort((a, b) => a.apellido.localeCompare(b.apellido)));
        } catch (err) {
            console.error("Error al cargar todos los espacios:", err);
            show_alerta('Error al cargar la información de todos los espacios', 'error');
        } finally {
            setLoadingTodos(false);
        }
    }, [cursoGrupo, division, cursosDisp, instanciaId]);

    // ── Helper para cargar ExcelJS dinámicamente ─────────────────────────
    const cargarExcelJS = () => {
        return new Promise((resolve, reject) => {
            if (window.ExcelJS) return resolve(window.ExcelJS);
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.3.0/exceljs.min.js';
            script.onload = () => resolve(window.ExcelJS);
            script.onerror = reject;
            document.body.appendChild(script);
        });
    };

    // ── Helper para convertir URL de imagen en Base64 con dimensiones original ────────
    const obtenerBase64Imagen = (url) => {
        return new Promise((resolve) => {
            if (!url) return resolve(null);
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/png');
                    resolve({
                        base64: dataURL,
                        width: img.width,
                        height: img.height
                    });
                } catch {
                    resolve(null);
                }
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });
    };

    // ── Exportar Matriz Completa a Excel con Logo Institucional Proporcional ─────────
    const exportarMatrizExcel = async () => {
        if (!matrizEstudiantes.length || !resumenEspacios.length) return;

        try {
            const ExcelJS = await cargarExcelJS();
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Matriz_General");

            // Cargar Logo Institucional
            const logoUrl = configuracion?.logo_solo ? getImgUrl(configuracion.logo_solo) : null;
            const logoObj = logoUrl ? await obtenerBase64Imagen(logoUrl) : null;

            let startRow = 1;

            if (logoObj && logoObj.base64) {
                const imageId = workbook.addImage({
                    base64: logoObj.base64,
                    extension: 'png',
                });

                const origW = logoObj.width || 120;
                const origH = logoObj.height || 60;
                const maxHeight = 55;
                const maxWidth = 150;
                const ratio = Math.min(maxWidth / origW, maxHeight / origH);
                const finalWidth = Math.round(origW * ratio);
                const finalHeight = Math.round(origH * ratio);

                worksheet.addImage(imageId, {
                    tl: { col: 0, row: 0 },
                    ext: { width: finalWidth, height: finalHeight }
                });
                startRow = 4;
            }

            worksheet.getCell(`B${startRow}`).value = `ESTADO GENERAL DE MATERIAS — ${instanciaNombre}`;
            worksheet.getCell(`B${startRow}`).font = { bold: true, size: 14, color: { argb: '0F172A' } };
            worksheet.getCell(`B${startRow + 1}`).value = `Cohorte: ${cohorteSelec?.nombre_formacion || ''} — ${cohorteSelec?.año || ''} | ${ordenSelec}° año, División ${division}`;
            worksheet.getCell(`B${startRow + 1}`).font = { size: 10, color: { argb: '64748B' } };

            worksheet.addRow([]);
            worksheet.addRow([]);

            const thinBorder = {
                top: { style: 'thin', color: { argb: 'CBD5E1' } },
                left: { style: 'thin', color: { argb: 'CBD5E1' } },
                bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
                right: { style: 'thin', color: { argb: 'CBD5E1' } }
            };

            const headers = ["Estudiante", ...resumenEspacios.map(r => r.materia.nombre_espacio)];
            const headerRow = worksheet.addRow(headers);
            headerRow.height = 24;
            headerRow.eachCell((cell, colNum) => {
                cell.font = { bold: true, color: { argb: 'FFFFFF' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '1E293B' }
                };
                cell.alignment = { vertical: 'middle', horizontal: colNum === 1 ? 'left' : 'center', wrapText: true };
                cell.border = thinBorder;
            });

            matrizEstudiantes.forEach(est => {
                const rowValues = [`${est.apellido}, ${est.nombre}`];
                resumenEspacios.forEach(r => {
                    const valInfo = est.valoraciones[r.materia.id];
                    const valStr = valInfo && valInfo.valor ? valInfo.valor : '—';
                    rowValues.push(valStr);
                });

                const addedRow = worksheet.addRow(rowValues);
                addedRow.height = 20;

                // Estilo celda del estudiante (columna 1)
                const studentCell = addedRow.getCell(1);
                studentCell.font = { bold: true, color: { argb: '0F172A' } };
                studentCell.alignment = { vertical: 'middle', horizontal: 'left' };
                studentCell.border = thinBorder;

                // Estilo celdas de materias (columnas 2 en adelante)
                resumenEspacios.forEach((r, idx) => {
                    const cell = addedRow.getCell(idx + 2);
                    const valInfo = est.valoraciones[r.materia.id];
                    const valStr = valInfo && valInfo.valor ? valInfo.valor : null;
                    const estadoVal = valInfo?.estado;
                    const isBaja = valStr ? esCalificacionBaja(valStr, r.materia.tipo_calificacion, configuracion) : false;
                    const isIndice2 = valStr ? esCalificacionIndice2(valStr, r.materia.tipo_calificacion, configuracion) : false;

                    let bgArgb = 'FFFFFF';
                    let fontArgb = '64748B';
                    let isBold = false;

                    if (estadoVal === 'publicada' || estadoVal === 'vista_por_tutor') {
                        bgArgb = 'CFE2FF'; // bg-primary-subtle
                        fontArgb = isBaja ? 'DC3545' : (isIndice2 ? 'B45309' : '084298');
                        isBold = true;
                    } else if (estadoVal === 'aprobada') {
                        bgArgb = 'D1E7DD'; // bg-success-subtle
                        fontArgb = isBaja ? 'DC3545' : (isIndice2 ? 'B45309' : '0F5132');
                        isBold = true;
                    } else if (valStr) {
                        isBold = true;
                        if (isBaja) {
                            bgArgb = 'F8D7DA'; // bg-danger-subtle
                            fontArgb = '842029';
                        } else if (isIndice2) {
                            bgArgb = 'FFF3CD'; // bg-warning-subtle
                            fontArgb = '664D03';
                        } else {
                            bgArgb = 'FFF3CD'; // bg-warning-subtle
                            fontArgb = '1E293B';
                        }
                    }

                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: bgArgb }
                    };
                    cell.font = {
                        bold: isBold,
                        color: { argb: fontArgb }
                    };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    cell.border = thinBorder;
                });
            });

            worksheet.columns.forEach((column, index) => {
                if (index === 0) {
                    column.width = 30;
                } else {
                    column.width = 18;
                }
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Matriz_Calificaciones_${ordenSelec}_Div_${division}_${instanciaNombre}.xlsx`;
            link.click();
        } catch (err) {
            console.error("Error al exportar matriz a Excel:", err);
            // Fallback
            const data = [];
            data.push([`ESTADO GENERAL DE MATERIAS — ${instanciaNombre}`]);
            data.push([`Cohorte: ${cohorteSelec?.nombre_formacion || ''} — ${cohorteSelec?.año || ''} | ${ordenSelec}° año, División ${division}`]);
            data.push([]);
            const headers = ["Estudiante", ...resumenEspacios.map(r => r.materia.nombre_espacio)];
            data.push(headers);
            matrizEstudiantes.forEach(est => {
                const row = [`${est.apellido}, ${est.nombre}`];
                resumenEspacios.forEach(r => {
                    const valInfo = est.valoraciones[r.materia.id];
                    const valStr = valInfo && valInfo.valor ? valInfo.valor : '—';
                    row.push(valStr);
                });
                data.push(row);
            });
            const ws = XLSX.utils.aoa_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Matriz_General");
            XLSX.writeFile(wb, `Matriz_Calificaciones_${ordenSelec}_Div_${division}_${instanciaNombre}.xlsx`);
        }
    };

    const cargarPanel = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.post(URL_ESTUDIANTES, {
                modo: 'buscarEstudiantesCurso',
                id_curso: cursoId,
                id_grupo: grupo,
                id_instancia: instanciaId,
            });
            if (res.data.estudiantes) {
                setEstudiantes(res.data.estudiantes);
                setValoraciones(res.data.valoraciones || []);
                setEquipoDocente(res.data.equipo_docente || []);

                // Cargar estados (aprobadas/publicadas) desde el servidor
                cargarEstados(grupo, cursoId, instanciaId);

                // Cargar trayectorias del grupo si existe
                if (grupo && grupo !== 'todos') {
                    try {
                        const resTray = await axios.get(`${CONFIG.API_URL}/operarTrayectorias.php?id_curso_grupo=${grupo}`);
                        if (Array.isArray(resTray.data)) {
                            setTrayectorias(resTray.data);
                        } else {
                            setTrayectorias([]);
                        }
                    } catch {
                        setTrayectorias([]);
                    }
                }
            } else {
                show_alerta('No se encontraron estudiantes para este grupo', 'warning');
                resetPanel();
            }
        } catch {
            show_alerta('Error al cargar los datos del grupo', 'error');
        } finally {
            setLoading(false);
        }
    }, [grupo, cursoId, instanciaId, cohorteId]);

    // ── Cargar estados de aprobación/publicación desde el servidor ────────
    const cargarEstados = async (idGrupo, idCurso, idInstancia) => {
        try {
            const res = await axios.get(`${URL_CALIFICACIONES}?accion=estados&id_grupo=${idGrupo}&id_curso=${idCurso}&id_instancia=${idInstancia}`);
            if (res.data.success) {
                setAprobadas(res.data.aprobadas || {});
                setPublicadas(res.data.publicadas || false);
            }
        } catch {
            // Si el endpoint no existe aún, simplemente ignoro
        }
    };

    // ── Aprobar / desaprobar calificación de un estudiante ────────────────
    const toggleAprobar = async (idUsuario) => {
        const nuevoEstado = !aprobadas[idUsuario];

        // Optimistic update
        setAprobadas(prev => ({ ...prev, [idUsuario]: nuevoEstado }));
        setValoraciones(prev => {
            const arr = Array.isArray(prev) ? prev : [];
            return arr.map(v => {
                if (String(v.id_usuario) === String(idUsuario)) {
                    return { ...v, estado_aprobacion: nuevoEstado ? 'aprobada' : null };
                }
                return v;
            });
        });

        try {
            await axios.post(URL_CALIFICACIONES, {
                accion: 'aprobar',
                id_grupo: grupo,
                id_curso: cursoId,
                id_instancia: instanciaId,
                id_usuario: idUsuario,
                aprobada: nuevoEstado,
            });
        } catch {
            // Revertir
            setAprobadas(prev => ({ ...prev, [idUsuario]: !nuevoEstado }));
            setValoraciones(prev => {
                const arr = Array.isArray(prev) ? prev : [];
                return arr.map(v => {
                    if (String(v.id_usuario) === String(idUsuario)) {
                        return { ...v, estado_aprobacion: !nuevoEstado ? 'aprobada' : null };
                    }
                    return v;
                });
            });
            show_alerta('Error al actualizar el estado', 'error');
        }
    };

    // ── Aprobar todas las calificaciones de una sola vez ───────────────────
    const aprobarTodasLasCalificaciones = async () => {
        Swal.fire({
            title: '¿Aprobar todas las calificaciones?',
            text: `¿Desea aprobar de manera masiva todas las calificaciones cargadas para la instancia "${instanciaNombre}"?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, aprobar todas',
            cancelButtonText: 'Cancelar',
            customClass: {
                confirmButton: 'btn btn-success me-2',
                cancelButton: 'btn btn-secondary'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await axios.post(`${CONFIG.API_URL}/operarValoraciones.php`, {
                        modo: 'aprobarValoracionesInstancia',
                        id_instancia: instanciaId,
                        id_curso: cursoId
                    });

                    const nextAprobadas = {};
                    estudiantes.forEach(est => {
                        nextAprobadas[est.id] = true;
                    });
                    setAprobadas(nextAprobadas);

                    setValoraciones(prev => {
                        const arr = Array.isArray(prev) ? prev : [];
                        return arr.map(v => ({ ...v, estado_aprobacion: 'aprobada' }));
                    });

                    Swal.fire('Éxito', res.data.message || 'Todas las valoraciones han sido aprobadas', 'success');
                    cargarPanel();
                } catch (err) {
                    console.error(err);
                    Swal.fire('Error', 'No se pudo aprobar masivamente', 'error');
                }
            }
        });
    };

    // ── Abrir modal de indicador de trayectoria y comentario editable / solo lectura ────────
    const abrirModalTrayectoriaComentario = (estudiante, valObj, trayectoria, soloLectura = false) => {
        const valorCalificacion = valObj?.valor || '';
        let observacionTxt = valObj?.observacion || '';

        if (!observacionTxt && trayectoria?.evaluaciones && Array.isArray(trayectoria.evaluaciones)) {
            const evalEst = trayectoria.evaluaciones.find(
                e => String(e.id_estudiante) === String(estudiante.id) && e.comentario && e.comentario.trim() !== ''
            );
            if (evalEst) {
                observacionTxt = evalEst.comentario;
            }
        }

        const crit = trayectoria?.criterios?.find(
            c => String(c.criterio).trim().toLowerCase() === String(valorCalificacion || '').trim().toLowerCase()
        );

        const tituloNivel = valorCalificacion 
            ? `Nivel alcanzado: <span class="badge bg-success-subtle text-success border border-success fw-bold px-2 py-1">${valorCalificacion}</span>` 
            : '<span class="badge bg-secondary-subtle text-secondary border px-2 py-1">Sin calificación registrada</span>';

        const descripcionCriterioHtml = crit && crit.descripcion 
            ? `<div class="p-3 bg-light rounded text-start border mt-2">
                <p class="mb-1 text-dark fw-bold small">Descripción del nivel alcanzado:</p>
                <p class="mb-0 text-secondary small" style="white-space: pre-line;">${crit.descripcion}</p>
               </div>`
            : `<div class="p-3 bg-light rounded text-start text-muted border mt-2 small">
                <em>${valorCalificacion ? 'No hay una descripción de logro cargada para este nivel en la trayectoria.' : 'Seleccione una calificación para visualizar su descripción.'}</em>
               </div>`;

        const comentarioSeccionHtml = soloLectura
            ? `<div class="mt-3 text-start">
                <label class="form-label fw-bold text-dark small mb-1">
                    <i class="fa-regular fa-comment-dots text-primary me-1"></i>Comentario / Observación:
                </label>
                <div class="p-2 bg-light rounded border text-secondary small" style="white-space: pre-line;">
                    ${observacionTxt ? observacionTxt : '<em>Sin observación registrada.</em>'}
                </div>
               </div>`
            : `<div class="mt-3 text-start">
                <label class="form-label fw-bold text-dark small mb-1">
                    <i class="fa-regular fa-comment-dots text-primary me-1"></i>Comentario / Observación:
                </label>
                <textarea id="swalComentarioCalGen" class="form-control form-control-sm" rows="3" placeholder="Ingrese una observación o comentario para el estudiante...">${observacionTxt}</textarea>
               </div>`;

        Swal.fire({
            title: `<span class="fs-5 text-dark fw-bold"><i class="fa-solid fa-route text-success me-2"></i>${trayectoria.titulo}</span>`,
            html: `
                <div class="text-start small">
                    <p class="mb-1 text-secondary"><strong>Estudiante:</strong> ${estudiante.nombre} ${estudiante.apellido}</p>
                    <p class="mb-2 text-secondary"><strong>Instancia:</strong> ${instanciaNombre || ''}</p>
                    <p class="mb-1">${tituloNivel}</p>
                    ${descripcionCriterioHtml}
                    ${comentarioSeccionHtml}
                </div>
            `,
            showCancelButton: !soloLectura,
            confirmButtonText: soloLectura ? 'Cerrar' : '<i class="fa-solid fa-floppy-disk me-1"></i>Guardar Comentario',
            cancelButtonText: 'Cancelar',
            customClass: {
                confirmButton: soloLectura ? 'btn btn-secondary rounded-pill px-4' : 'btn btn-success rounded-pill px-4 me-2',
                cancelButton: 'btn btn-secondary rounded-pill px-4'
            },
            buttonsStyling: false,
            preConfirm: () => {
                if (soloLectura) return null;
                const el = document.getElementById('swalComentarioCalGen');
                return el ? el.value : '';
            }
        }).then((result) => {
            if (!soloLectura && result.isConfirmed && result.value !== null) {
                const nuevoComentario = result.value;
                handleObservacionChange(estudiante.id, nuevoComentario);
                axios.post(`${CONFIG.API_URL}/operarValoraciones.php`, {
                    modo: 'guardarInformeValoraciones',
                    id_estudiante: estudiante.id,
                    id_instancia: instanciaId,
                    id_curso: cursoId,
                    informe: nuevoComentario
                })
                .then(res => {
                    if (!res.data.error) {
                        Swal.fire('Éxito', 'Comentario guardado correctamente', 'success');
                        cargarPanel();
                    } else {
                        Swal.fire('Error', res.data.message || 'No se pudo guardar el comentario', 'error');
                    }
                })
                .catch(err => {
                    console.error(err);
                    Swal.fire('Error', 'Error al conectar con el servidor', 'error');
                });
            }
        });
    };

    // ── Abrir modal para supervisar y controlar trayectoria y criterios ──────
    const abrirModalDetalleTrayectoria = (nombreEspacio, trayectoria) => {
        if (!trayectoria) return;

        const nivelOrder = { 'sobresaliente': 1, 'ms': 1, 'sup': 1, 'muy bueno': 2, 's': 2, 'bueno': 3, 'ep': 3, 'regular': 4, 'sc': 4, 'pendiente': 5 };
        const criterios = trayectoria.criterios ? [...trayectoria.criterios] : [];
        criterios.sort((a, b) => {
            const nameA = String(a.criterio || '').trim().toLowerCase();
            const nameB = String(b.criterio || '').trim().toLowerCase();
            const orderA = nivelOrder[nameA] !== undefined ? nivelOrder[nameA] : 99;
            const orderB = nivelOrder[nameB] !== undefined ? nivelOrder[nameB] : 99;
            if (orderA !== orderB) return orderA - orderB;
            return (Number(b.id) || 0) - (Number(a.id) || 0);
        });
        
        let criteriosHtml = '';
        if (criterios.length > 0) {
            criteriosHtml = criterios.map((crit) => `
                <div class="p-3 mb-2 rounded text-start border shadow-sm" style="background-color: #ffffff; border-left: 4px solid #10b981 !important;">
                    <div class="fw-bold text-dark d-flex align-items-center gap-2 mb-1" style="font-size: 0.95rem;">
                        <span class="badge bg-success-subtle text-success border border-success px-2 py-1">${crit.criterio}</span>
                    </div>
                    <div class="text-secondary small" style="white-space: pre-line; line-height: 1.4;">
                        ${crit.descripcion && crit.descripcion.trim() !== '' ? crit.descripcion : '<em class="text-muted">Sin descripción especificada para este nivel.</em>'}
                    </div>
                </div>
            `).join('');
        } else {
            criteriosHtml = `
                <div class="p-3 bg-light rounded text-center text-muted border small">
                    <em>No hay criterios ni niveles de logro definidos para esta trayectoria.</em>
                </div>
            `;
        }

        const descripcionHtml = trayectoria.descripcion && trayectoria.descripcion.trim() !== ''
            ? `<div class="p-3 bg-light rounded text-start border mb-3">
                <strong class="text-dark small d-block mb-1">
                    <i class="fa-solid fa-align-left me-1 text-primary"></i>Descripción general del Registro de Trayectoria:
                </strong>
                <span class="text-secondary small" style="white-space: pre-line;">${trayectoria.descripcion}</span>
               </div>`
            : `<div class="p-2 bg-light rounded text-start border mb-3 small text-muted">
                <em>Sin descripción general registrada por el docente.</em>
               </div>`;

        Swal.fire({
            title: `<span class="fs-5 text-dark fw-bold"><i class="fa-solid fa-route text-success me-2"></i>${trayectoria.titulo || 'Registro de Trayectoria'}</span>`,
            html: `
                <div class="text-start small">
                    <div class="d-flex flex-wrap gap-2 mb-3">
                        <span class="badge bg-primary-subtle text-primary border px-2 py-1" style="font-size: 0.78rem;">
                            <i class="fa-solid fa-book me-1"></i>${nombreEspacio}
                        </span>
                        <span class="badge bg-info-subtle text-info border px-2 py-1" style="font-size: 0.78rem;">
                            <i class="fa-solid fa-calendar-check me-1"></i>${instanciaNombre || 'Instancia seleccionada'}
                        </span>
                        <span class="badge bg-light text-dark border px-2 py-1" style="font-size: 0.78rem;">
                            <i class="fa-solid fa-list-check text-success me-1"></i>${criterios.length} ${criterios.length === 1 ? 'nivel' : 'niveles'} de logro
                        </span>
                    </div>

                    ${descripcionHtml}

                    <div class="mt-3">
                        <h6 class="fw-bold text-dark mb-2 small d-flex justify-content-between align-items-center">
                            <span><i class="fa-solid fa-list-check text-success me-1"></i>Criterios y Descripciones de Logro Redactadas:</span>
                        </h6>
                        <div style="max-height: 280px; overflow-y: auto;" class="pe-1">
                            ${criteriosHtml}
                        </div>
                    </div>
                </div>
            `,
            confirmButtonText: 'Cerrar',
            customClass: {
                confirmButton: 'btn btn-secondary rounded-pill px-4'
            },
            buttonsStyling: false,
            width: '620px'
        });
    };

    // ── Publicar todas las calificaciones ────────────────────────────────
    const publicarCalificaciones = async () => {
        const todasAprobadas = estudiantes.every(est => {
            const val = getValoracion(est.id);
            return val && aprobadas[est.id];
        });

        if (!todasAprobadas) {
            show_alerta('Todas las calificaciones deben estar cargadas y aprobadas para poder publicar', 'warning');
            return;
        }

        try {
            const res = await axios.post(URL_CALIFICACIONES, {
                accion: 'publicar',
                id_grupo: grupo,
                id_curso: cursoId,
                id_instancia: instanciaId,
            });
            if (res.data.success) {
                setPublicadas(true);
                setAprobadas(prev => {
                    const next = { ...prev };
                    estudiantes.forEach(est => {
                        next[est.id] = true;
                    });
                    return next;
                });
                setValoraciones(prev => {
                    const arr = Array.isArray(prev) ? prev : [];
                    return arr.map(v => ({ ...v, estado_aprobacion: 'publicada' }));
                });
                show_alerta('Calificaciones publicadas correctamente', 'success');
            } else {
                show_alerta(res.data.mensaje || 'Error al publicar', 'error');
            }
        } catch {
            show_alerta('Error de conexión al publicar', 'error');
        }
    };

    // ── Manejo de observaciones ──────────────────────────────────────────
    const handleObservacionChange = (idUsuario, nuevaObs) => {
        setValoraciones(prev => {
            const arr = Array.isArray(prev) ? prev : [];
            const exists = arr.some(v => String(v.id_usuario) === String(idUsuario));
            if (exists) {
                return arr.map(v =>
                    String(v.id_usuario) === String(idUsuario)
                        ? { ...v, observacion: nuevaObs }
                        : v
                );
            } else {
                return [...arr, {
                    id_usuario: idUsuario,
                    id_instancia: instanciaId,
                    id_curso: cursoId,
                    valor: '',
                    observacion: nuevaObs,
                    estado_aprobacion: null
                }];
            }
        });
    };

    const handleObservacionBlur = async (idUsuario) => {
        const val = getValoracion(idUsuario);
        const observacion = val?.observacion || '';

        try {
            await axios.post(`${CONFIG.API_URL}/operarValoraciones.php`, {
                modo: 'guardarInformeValoraciones',
                id_estudiante: idUsuario,
                id_instancia: instanciaId,
                id_curso: cursoId,
                informe: observacion
            });
        } catch {
            show_alerta('Error al guardar la observación', 'error');
        }
    };

    // ── Helper: obtener valoración de un estudiante ───────────────────────
    const getValoracion = (idUsuario) => {
        if (!Array.isArray(valoraciones)) return undefined;
        return valoraciones.find(v => v.id_usuario === idUsuario || v.id_usuario === String(idUsuario));
    };

    // ── Resumen de estados ────────────────────────────────────────────────
    const resumen = {
        total: estudiantes.length,
        sinCargar: estudiantes.filter(e => { const v = getValoracion(e.id); return !v || v.valor === null || v.valor === ''; }).length,
        cargadas: estudiantes.filter(e => {
            const v = getValoracion(e.id);
            const tieneVal = v && v.valor !== null && v.valor !== '' && String(v.valor).trim() !== '';
            const esAprob = !!aprobadas[e.id] || (v && (v.estado_aprobacion === 'aprobada' || v.estado_aprobacion === 'publicada' || v.estado_aprobacion === 'vista_por_tutor'));
            return tieneVal && !esAprob;
        }).length,
        aprobadas: estudiantes.filter(e => {
            const v = getValoracion(e.id);
            const esAprob = !!aprobadas[e.id] || (v && (v.estado_aprobacion === 'aprobada' || v.estado_aprobacion === 'publicada' || v.estado_aprobacion === 'vista_por_tutor'));
            const esPub = publicadas || (v && (v.estado_aprobacion === 'publicada' || v.estado_aprobacion === 'vista_por_tutor'));
            return esAprob && !esPub;
        }).length,
        publicadas: publicadas ? estudiantes.length : estudiantes.filter(e => {
            const v = getValoracion(e.id);
            return v && (v.estado_aprobacion === 'publicada' || v.estado_aprobacion === 'vista_por_tutor');
        }).length,
    };

    const todasListasParaPublicar =
        resumen.total > 0 &&
        resumen.sinCargar === 0 &&
        resumen.aprobadas + resumen.publicadas === resumen.total;

    const todasNotasCargadas =
        estudiantes.length > 0 &&
        estudiantes.every(est => {
            const v = getValoracion(est.id);
            return v && v.valor !== null && v.valor !== undefined && String(v.valor).trim() !== '';
        });

    // ── Datos de la cohorte y curso seleccionados (para el header) ────────
    const cohorteSelec = cohortes.find(c => String(c.id) === String(cohorteId));
    const [ordenSelec] = cursoGrupo ? cursoGrupo.split('-') : [''];
    const selectedMateriaObj = cursosDisp.find(c => String(c.id) === String(grupo));
    const materiaNombre = grupo === 'todos' ? 'Todos los espacios' : (selectedMateriaObj ? selectedMateriaObj.nombre_espacio : '');
    const selectedInstanciaObj = instancias.find(i => String(i.id) === String(instanciaId));
    const instanciaNombre = selectedInstanciaObj ? selectedInstanciaObj.nombre : '';

    const trayEncontrada = trayectorias.find(t => String(t.id_instancia) === String(instanciaId));
    const estadoInstancias = obtenerEstadoInstancias(instancias.length > 0 ? instancias : instanciasDefault);

    // ══════════════════════════════════════════════════════════════════════
    return (
        <div className="container-principal">
            {/* ── Título ── */}
            <h2 className="calgen-titulo">
                <i className="fa-solid fa-graduation-cap"></i>
                Calificaciones Generales
            </h2>
            <p className="calgen-subtitulo">
                Control de calificaciones y observaciones docentes
            </p>

            {estadoInstancias && (
                estadoInstancias.abierta ? (
                    <div className="alert alert-success py-2 px-3 small mb-3 d-flex align-items-center flex-wrap gap-2" role="alert">
                        <i className="fa-solid fa-lock-open me-1"></i>
                        <span>
                            <strong>Instancia abierta:</strong>{' '}
                            {estadoInstancias.abiertas.map((inst, idx) => (
                                <span key={inst.id || idx}>
                                    {idx > 0 && ' • '}
                                    <strong>{inst.nombre}</strong> (hasta el {inst.cierreFormateado})
                                </span>
                            ))}
                        </span>
                    </div>
                ) : (
                    <div className="alert alert-warning py-2 px-3 small mb-3 d-flex align-items-center flex-wrap gap-2" role="alert">
                        <i className="fa-solid fa-lock me-1"></i>
                        <span><strong>Todas las instancias están cerradas.</strong></span>
                        {estadoInstancias.ultimaCerrada && (
                            <span>
                                • <strong>Última instancia:</strong> {estadoInstancias.ultimaCerrada.nombre} (cerró el {estadoInstancias.ultimaCerrada.cierreFormateado})
                            </span>
                        )}
                        {estadoInstancias.proximaAbrir && (
                            <span>
                                • <strong>Próxima instancia:</strong> {estadoInstancias.proximaAbrir.nombre} (abre el {estadoInstancias.proximaAbrir.inicioFormateado})
                            </span>
                        )}
                    </div>
                )
            )}

            {/* ── Filtros ── */}
            <div className="calgen-filtros">
                {/* Cohorte */}
                <div className="filtro-grupo">
                    <label htmlFor="calgen-cohorte">Cohorte</label>
                    <select
                        id="calgen-cohorte"
                        value={cohorteId}
                        onChange={e => setCohorteId(e.target.value)}
                    >
                        <option value="">-- Seleccionar cohorte --</option>
                        {cohortes.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.nombre_formacion} — {c.año}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Curso */}
                <div className="filtro-grupo">
                    <label htmlFor="calgen-curso">Curso</label>
                    <select
                        id="calgen-curso"
                        value={cursoGrupo}
                        onChange={e => setCursoGrupo(e.target.value)}
                        disabled={!cohorteId || cursosDisp.length === 0}
                    >
                        <option value="">-- Seleccionar curso --</option>
                        {/* Mostrar cursos únicos por orden */}
                        {[...new Map(cursosDisp.map(c => [c.orden, c])).values()].map((c, i) => (
                            <option key={i} value={`${c.orden}-${c.seccion}`}>
                                {c.orden}° año
                            </option>
                        ))}
                    </select>
                </div>

                {/* Grupo / División */}
                <div className="filtro-grupo">
                    <label htmlFor="calgen-division">Grupo / División</label>
                    <select
                        id="calgen-division"
                        value={division}
                        onChange={e => setDivision(e.target.value)}
                        disabled={!cursoGrupo || gruposDisp.length === 0}
                    >
                        <option value="">-- Seleccionar división --</option>
                        {gruposDisp.map((g, i) => (
                            <option key={i} value={g.seccion}>
                                División {g.denominacion}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Instancia de Calificación */}
                <div className="filtro-grupo">
                    <label htmlFor="calgen-instancia">Instancia</label>
                    <select
                        id="calgen-instancia"
                        value={instanciaId}
                        onChange={e => setInstanciaId(e.target.value)}
                        disabled={!division || instancias.length === 0}
                    >
                        <option value="">-- Seleccionar instancia --</option>
                        {instancias.map((inst) => (
                            <option key={inst.id} value={inst.id}>
                                {inst.nombre}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Espacio Curricular */}
                <div className="filtro-grupo">
                    <label htmlFor="calgen-materia">Espacio</label>
                    <select
                        id="calgen-materia"
                        value={grupo}
                        onChange={e => {
                            const val = e.target.value;
                            setGrupo(val);
                            if (val === 'todos') {
                                setCursoId('todos');
                            } else if (val) {
                                const [orden] = cursoGrupo.split('-');
                                const materiasDisp = cursosDisp.filter(
                                    c => String(c.orden) === String(orden) && String(c.seccion) === String(division)
                                );
                                const match = materiasDisp.find(m => String(m.id) === String(val));
                                setCursoId(match ? match.id_curso : '');
                            } else {
                                setCursoId('');
                            }
                        }}
                        disabled={!instanciaId}
                    >
                        <option value="">-- Seleccionar espacio --</option>
                        {cursoGrupo && division && (
                            <option value="todos" style={{ fontWeight: 'bold', color: '#6366f1' }}>
                                -- Todos los espacios --
                            </option>
                        )}
                        {cursoGrupo && division && cursosDisp
                            .filter(c => {
                                const [orden] = cursoGrupo.split('-');
                                return String(c.orden) === String(orden) && String(c.seccion) === String(division);
                            })
                            .map((m, i) => (
                                <option key={i} value={m.id}>
                                    {m.nombre_espacio}
                                </option>
                            ))}
                    </select>
                </div>
            </div>

            {/* ── Panel principal ── */}
            {grupo === 'todos' ? (
                loadingTodos ? (
                    <div className="calgen-loading">
                        <i className="fa-solid fa-spinner fa-spin me-2"></i>
                        Analizando el estado de todos los espacios curriculares...
                    </div>
                ) : (
                    <div className="calgen-panel">
                        {/* Header del panel */}
                        <div className="calgen-panel-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <div>
                                <span className="calgen-panel-titulo">
                                    <i className="fa-solid fa-layer-group me-2 text-primary"></i>
                                    Estado de Carga de Todos los Espacios Curriculares — {instanciaNombre}
                                </span>
                                <div className="small text-muted mt-1">
                                    {cohorteSelec?.nombre_formacion} — {cohorteSelec?.año} &nbsp;|&nbsp; {ordenSelec}° año, División {division}
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    className={`btn btn-sm ${modoVerMatriz ? 'btn-primary' : 'btn-outline-ligth'}`}
                                    onClick={() => setModoVerMatriz(!modoVerMatriz)}
                                >
                                    <i className={`fa-solid ${modoVerMatriz ? 'fa-th-large me-1' : 'fa-table me-1'}`}></i>
                                    {modoVerMatriz ? 'Ver Tarjetas por Espacio' : 'Ver Todos los Alumnos x Materias'}
                                </button>
                                {modoVerMatriz && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-success"
                                        onClick={exportarMatrizExcel}
                                        title="Exportar Matriz Completa a Excel"
                                    >
                                        <i className="fa-solid fa-file-excel me-1"></i>
                                        Exportar Excel
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Chips Resumen de Estados */}
                        <div className="d-flex flex-wrap gap-2 p-3 bg-light border-bottom">
                            <span className="badge badge-estado-violeta p-2 rounded-pill shadow-sm">
                                <i className="fa-solid fa-circle-check me-1"></i>
                                Violeta: {resumenEspacios.filter(r => r.estadoCodigo === 'violeta').length} Publicados
                            </span>
                            <span className="badge badge-estado-verde p-2 rounded-pill shadow-sm">
                                <i className="fa-solid fa-thumbs-up me-1"></i>
                                Verde: {resumenEspacios.filter(r => r.estadoCodigo === 'verde').length} Aceptados (sin publicar)
                            </span>
                            <span className="badge badge-estado-amarillo p-2 rounded-pill shadow-sm">
                                <i className="fa-solid fa-clock me-1"></i>
                                Amarillo: {resumenEspacios.filter(r => r.estadoCodigo === 'amarillo').length} Cargados (pend. aprobación)
                            </span>
                            <span className="badge badge-estado-rojo p-2 rounded-pill shadow-sm">
                                <i className="fa-solid fa-triangle-exclamation me-1"></i>
                                Rojo: {resumenEspacios.filter(r => r.estadoCodigo === 'rojo').length} Incompletos
                            </span>
                        </div>

                        {!modoVerMatriz ? (
                            /* Vista de Tarjetas por Espacio */
                            <div className="row g-3 p-3">
                                {resumenEspacios.map((item, idx) => (
                                    <div key={idx} className="col-12 col-md-6 col-lg-4">
                                        <div className={`card h-100 shadow-sm ${item.cardClass}`}>
                                            <div className="card-body d-flex flex-column justify-content-between">
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                                        <h5 className="card-title fw-bold text-dark mb-0 fs-6">
                                                            {item.materia.nombre_espacio}
                                                        </h5>
                                                        <span className={`badge ${item.badgeClass} p-2 rounded-circle shadow-sm`} title={item.estadoTexto} style={{ fontSize: '0.85rem' }}>
                                                            <i className={`fa-solid ${ESTADO_ICONO[item.estadoCodigo] || 'fa-circle-info'}`}></i>
                                                        </span>
                                                    </div>

                                                    {item.equipoDocente && item.equipoDocente.length > 0 && (
                                                        <div className="small text-secondary mb-2">
                                                            <i className="fa-solid fa-chalkboard-user me-1 text-primary"></i>
                                                            {item.equipoDocente.map(d => `${d.apellido}, ${d.nombre}`).join(' | ')}
                                                        </div>
                                                    )}

                                                    <div className="bg-light p-2 rounded border my-2 small">
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span>Notas cargadas:</span>
                                                            <strong>{item.cargadas} / {item.totalEst}</strong>
                                                        </div>
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <span>Aprobadas por gestión:</span>
                                                            <strong>{item.aprobadas} / {item.totalEst}</strong>
                                                        </div>
                                                        <div className="d-flex justify-content-between">
                                                            <span>Publicadas:</span>
                                                            <strong>{item.publicadas} / {item.totalEst}</strong>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm w-100 mt-2"
                                                    onClick={() => {
                                                        setGrupo(String(item.materia.id));
                                                        setCursoId(String(item.materia.id_curso));
                                                    }}
                                                >
                                                    <i className="fa-solid fa-eye me-1"></i>
                                                    Gestionar esta materia
                                                </button>

                                                {item.trayectoria && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-success btn-sm w-100 mt-2"
                                                        onClick={() => abrirModalDetalleTrayectoria(item.materia.nombre_espacio, item.trayectoria)}
                                                        title="Ver criterios y detalle de la trayectoria redactada por el docente"
                                                    >
                                                        <i className="fa-solid fa-route me-1"></i>
                                                        Ver Trayectoria y Criterios
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            /* Vista de Matriz Completa de Alumnos x Materias */
                            <div className="table-responsive p-3">
                                <table className="table table-bordered table-hover align-middle mb-0 small">
                                    <thead className="table-dark">
                                        <tr>
                                            <th style={{ minWidth: '180px', verticalAlign: 'bottom' }}>Estudiante</th>
                                            {resumenEspacios.map((r, i) => (
                                                <th key={i} className="th-materia-vertical text-center align-bottom">
                                                    <div className="d-flex flex-column align-items-center justify-content-between h-100 py-1">
                                                        <span className={`badge ${r.badgeClass} p-2 rounded-circle shadow-sm mb-1`} title={`${r.materia.nombre_espacio}: ${r.estadoTexto}`} style={{ fontSize: '0.85rem' }}>
                                                            <i className={`fa-solid ${ESTADO_ICONO[r.estadoCodigo] || 'fa-circle-info'}`}></i>
                                                        </span>
                                                        <div className="th-materia-vertical-text" title={r.materia.nombre_espacio}>
                                                            {r.materia.nombre_espacio}
                                                        </div>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {matrizEstudiantes.map((est) => (
                                            <tr key={est.id}>
                                                <td className="fw-semibold text-dark">
                                                    {est.apellido}, {est.nombre}
                                                </td>
                                                {resumenEspacios.map((r, i) => {
                                                    const valInfo = est.valoraciones[r.materia.id];
                                                    const valStr = valInfo && valInfo.valor ? valInfo.valor : null;
                                                    const estadoVal = valInfo?.estado;
                                                    const isBaja = valStr ? esCalificacionBaja(valStr, r.materia.tipo_calificacion, configuracion) : false;
                                                    const isIndice2 = valStr ? esCalificacionIndice2(valStr, r.materia.tipo_calificacion, configuracion) : false;

                                                    let bgStyle = '';
                                                    let textStyle = '';

                                                    if (estadoVal === 'publicada' || estadoVal === 'vista_por_tutor') {
                                                        bgStyle = 'bg-primary-subtle';
                                                        textStyle = isBaja ? 'text-danger fw-bold' : (isIndice2 ? 'text-warning-emphasis fw-bold' : 'text-primary fw-bold');
                                                    } else if (estadoVal === 'aprobada') {
                                                        bgStyle = 'bg-success-subtle';
                                                        textStyle = isBaja ? 'text-danger fw-bold' : (isIndice2 ? 'text-warning-emphasis fw-bold' : 'text-success fw-bold');
                                                    } else if (valStr) {
                                                        if (isBaja) {
                                                            bgStyle = 'bg-danger-subtle';
                                                            textStyle = 'text-danger fw-bold';
                                                        } else if (isIndice2) {
                                                            bgStyle = 'bg-warning-subtle';
                                                            textStyle = 'text-warning-emphasis fw-bold';
                                                        } else {
                                                            bgStyle = 'bg-warning-subtle';
                                                            textStyle = 'text-dark fw-bold';
                                                        }
                                                    }

                                                    return (
                                                        <td key={i} style={{ textAlign: 'center' }} className={`${bgStyle} ${textStyle}`}>
                                                            {valStr ? (
                                                                <div className="d-flex align-items-center justify-content-center gap-1">
                                                                    <span>{valStr}</span>
                                                                    {estadoVal === 'vista_por_tutor' && (
                                                                        <i className="fa-solid fa-eye text-success small" title="Vista por el tutor"></i>
                                                                    )}
                                                                    {estadoVal === 'publicada' && (
                                                                        <i className="fa-solid fa-bullhorn text-primary small" title="Publicada"></i>
                                                                    )}
                                                                    {estadoVal === 'aprobada' && (
                                                                        <i className="fa-solid fa-thumbs-up text-success small" title="Aprobada por gestión"></i>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">—</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )
            ) : loading ? (
                <div className="calgen-loading">
                    <i className="fa-solid fa-spinner"></i>
                    Cargando datos...
                </div>
            ) : estudiantes.length === 0 ? (
                <div className="calgen-empty">
                    <i className="fa-solid fa-table-list"></i>
                    {cohorteId && cursoGrupo && division && instanciaId && grupo
                        ? 'No hay estudiantes en este grupo/materia/instancia'
                        : 'Seleccioná cohorte, curso, división, instancia y materia para ver las calificaciones'}
                </div>
            ) : (
                <div className="calgen-panel">
                    {/* ── Header del panel ── */}
                    <div className="calgen-panel-header">
                        <span className="calgen-panel-titulo">
                            <i className="fa-solid fa-users me-2"></i>
                            {cohorteSelec?.nombre_formacion} — {cohorteSelec?.año} &nbsp;|&nbsp;
                            {ordenSelec}° año, División {division} &nbsp;|&nbsp; {materiaNombre} &nbsp;|&nbsp; {instanciaNombre}
                        </span>
                        <span className="calgen-panel-meta">
                            {resumen.total} estudiante{resumen.total !== 1 ? 's' : ''} &nbsp;·&nbsp;
                            {resumen.sinCargar} sin cargar &nbsp;·&nbsp;
                            {resumen.cargadas} cargadas &nbsp;·&nbsp;
                            {resumen.aprobadas} aprobadas &nbsp;·&nbsp;
                            {resumen.publicadas} publicadas
                        </span>
                    </div>

                    {/* ── Equipo docente ── */}
                    {equipoDocente.length > 0 && (
                        <div className="calgen-docentes-section">
                            <div className="calgen-docentes-titulo">Equipo docente</div>
                            <div className="calgen-docentes-lista">
                                {equipoDocente.map(doc => (
                                    <span key={doc.id} className="calgen-docente-chip">
                                        <i className="fa-solid fa-chalkboard-user"></i>
                                        {doc.apellido}, {doc.nombre}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── Tabla de estudiantes ── */}
                    <div className="calgen-table-wrapper">
                        <table className="calgen-table">
                            <thead>
                                <tr>
                                    <th className="col-accion" style={{ textAlign: 'center' }}>Boletín</th>
                                    {!esPreceptor && (
                                        <th className="col-accion" style={{ textAlign: 'center' }}>
                                            Aprobar
                                            {todasNotasCargadas && (
                                                <button
                                                    className="btn btn-sm btn-link text-success p-0 ms-1"
                                                    onClick={aprobarTodasLasCalificaciones}
                                                    title="Aprobar todas las calificaciones cargadas de esta instancia de una sola vez"
                                                    type="button"
                                                >
                                                    <i className="fa-solid fa-thumbs-up"></i>
                                                </button>
                                            )}
                                        </th>
                                    )}
                                    <th>Estudiante</th>
                                    <th>Calificación</th>
                                    <th>Observación</th>
                                    <th>Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {estudiantes.map(est => {
                                    const val = getValoracion(est.id);
                                    const tieneCal = val && val.valor !== null && val.valor !== '' && String(val.valor).trim() !== '';
                                    const esAprobada = !!aprobadas[est.id] || (val && (val.estado_aprobacion === 'aprobada' || val.estado_aprobacion === 'publicada' || val.estado_aprobacion === 'vista_por_tutor'));
                                    const esPublicada = publicadas || (val && (val.estado_aprobacion === 'publicada' || val.estado_aprobacion === 'vista_por_tutor'));
                                    const esVistaPorTutor = val && val.estado_aprobacion === 'vista_por_tutor';
                                    const estado = calcularEstado(val, esAprobada, esPublicada);
                                    const valorNum = tieneCal ? parseFloat(val.valor) : null;

                                    return (
                                        <tr key={est.id}>
                                            {/* Botón Imprimir Boletín */}
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    className="calgen-btn-imprimir"
                                                    onClick={() => window.open(`${CONFIG.BASE_URL}ImprimirBoletin/${est.id}/${cohorteId}?instancia=${instanciaId}`, '_blank')}
                                                    title="Imprimir boletín de calificaciones completo"
                                                >
                                                    <i className="fa-solid fa-print"></i>
                                                </button>
                                            </td>

                                            {/* Botón Aprobar */}
                                            {!esPreceptor && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        className={`calgen-btn-aprobar${esAprobada ? ' aprobado' : ''}`}
                                                        onClick={() => toggleAprobar(est.id)}
                                                        disabled={!tieneCal || esPublicada}
                                                        title={esAprobada ? (esPublicada ? 'Calificación publicada' : 'Quitar aprobación') : 'Aprobar calificación'}
                                                    >
                                                        <i className="fa-solid fa-thumbs-up"></i>
                                                    </button>
                                                </td>
                                            )}

                                            {/* Nombre */}
                                            <td>
                                                <div className="calgen-nombre">
                                                    {est.apellido}, {est.nombre}
                                                </div>
                                                {est.apodo && (
                                                    <div className="calgen-apodo">"{est.apodo}"</div>
                                                )}
                                            </td>

                                            {/* Calificación */}
                                            <td>
                                                {tieneCal ? (
                                                    <span
                                                        className={`calgen-valor ${
                                                            esCalificacionBaja(val.valor, instancias.find(ins => String(ins.id) === String(instanciaId))?.tipo_calificacion, configuracion)
                                                                ? 'reprobado'
                                                                : (esCalificacionIndice2(val.valor, instancias.find(ins => String(ins.id) === String(instanciaId))?.tipo_calificacion, configuracion)
                                                                    ? 'text-warning-emphasis text-warning fw-bold'
                                                                    : (valorNum !== null && valorNum >= 6 ? 'aprobado' : ''))
                                                        }`}
                                                    >
                                                        {val.valor}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>—</span>
                                                )}
                                            </td>

                                            {/* Observación */}
                                            <td>
                                                {trayEncontrada ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                                                        onClick={() => abrirModalTrayectoriaComentario(est, val, trayEncontrada, esAprobada || esPublicada || esPreceptor)}
                                                        title="Ver indicador de trayectoria y comentario"
                                                    >
                                                        <i className="fa-solid fa-route"></i>
                                                        <span style={{ fontSize: '0.8rem' }}>
                                                            {esAprobada || esPublicada || esPreceptor
                                                                ? 'Ver Descripción'
                                                                : (val?.observacion && String(val.observacion).trim() !== '' ? 'Editar Comentario' : 'Agregar Comentario')
                                                            }
                                                        </span>
                                                    </button>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        className="calgen-input-obs"
                                                        value={val?.observacion || ''}
                                                        onChange={(e) => handleObservacionChange(est.id, e.target.value)}
                                                        onBlur={() => handleObservacionBlur(est.id)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                        disabled={esAprobada || esPublicada || esPreceptor}
                                                        placeholder="Escribir observación..."
                                                    />
                                                )}
                                            </td>

                                            {/* Estado */}
                                            <td>
                                                <BadgeEstado estado={estado} esVistaPorTutor={esVistaPorTutor} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Footer / Publicar ── */}
                    {!esPreceptor && (
                        <div className="calgen-footer">
                            <div className="calgen-footer-info">
                                {todasListasParaPublicar
                                    ? <span className="text-success fw-semibold"><i className="fa-solid fa-circle-check me-1"></i>Todas las calificaciones están aprobadas y listas para publicar.</span>
                                    : <span>Aprobá todas las calificaciones cargadas para habilitar la publicación.</span>
                                }
                            </div>
                            <button
                                className="calgen-btn-publicar"
                                onClick={publicarCalificaciones}
                                disabled={!todasListasParaPublicar || publicadas}
                            >
                                <i className="fa-solid fa-bullhorn"></i>
                                {publicadas ? 'Calificaciones publicadas' : 'Publicar calificaciones'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CalificacionesGeneral;
